import React, { useRef } from 'react'
import { useOpacityToggle } from '@hooks'
import { cn, useShrinkTextToFit } from '@amusnet/react-ui'
import {
    useBuyBonusStore,
    useConfigurationStore,
    useCountdownFSBonusStore,
    useFreeBuyBonusStore,
    useFreeSpinsBonusesStore,
    useLuckySpinsStore,
    useNavigationStore,
    useSpinButtonSectionStore,
} from '@stores'
import { withAutoBlur } from '@utils/misc'
import buyBonusImage from '@assets/buy-bonus/button-buybonus-freespins-default-enabled.svg?inline'
import buyBonusActiveImage from '@assets/buy-bonus/button-buybonus-active-enabled.svg?inline'
import buyBonusDefaultImage from '@assets/buy-bonus/button-buybonus-default-enabled.svg?inline'
import buyBonusDefaultCompactImage from '@assets/buy-bonus/button-buy-bonus-small.svg?inline'
import bonusFeatureIcon from '@assets/buy-bonus/buy-bonus-button-image-card-00.png?inline'
import { buildAtlasImageStyles } from '@utils/buildAtlasImageStyles'

import { ChangeOpacityComponents } from 'game-ui-types'
import { useAmuseBoostPromoWidgetPositions } from '@hooks/useAmuseBoostPromoWidgetPositions'

interface BuyBonusButtonProps {
    onClick: () => void
    isCompact?: boolean
}

const SELECTED_FEATURE_IMAGE_NAME = 'buy-bonus-button-image-card-01.png'

// TODO: move this function in misc.ts
export const renderMultilineText = (
    text: string,
    firstWordStyle?: React.CSSProperties,
    restWordStyle?: React.CSSProperties
): React.JSX.Element[] => {
    const [firstWord, ...restWords] = text.split(' ')

    return [
        <div key="first" style={firstWordStyle}>
            {firstWord}
        </div>,
        ...restWords.map((word, index) => (
            <div key={index} style={restWordStyle}>
                {word}
            </div>
        )),
    ]
}

const BuyBonusButton: React.FC<BuyBonusButtonProps> = ({ onClick, isCompact }) => {
    const buyBonusConfig = useConfigurationStore((s) => s.buyBonusConfig)
    const shouldTheFeatureStayActive = useConfigurationStore((s) => s.buyBonusConfig.shouldTheFeatureStayActive)

    const isAutoplaySectionOpened = useSpinButtonSectionStore((s) => s.isAutoplaySectionOpened)
    const isBetSectionOpened = useSpinButtonSectionStore((s) => s.isBetSectionOpened)
    const isLongPress = useSpinButtonSectionStore((s) => s.isLongPress)
    const isSpaceKeyPressed = useSpinButtonSectionStore((s) => s.isSpaceKeyPressed)

    const isFSBModalOpened = useFreeSpinsBonusesStore((s) => s.isModalOpened)
    const isFBBModalOpened = useFreeBuyBonusStore((s) => s.isModalOpened)
    const isLSBModalOpened = useLuckySpinsStore((s) => s.isModalOpened)
    const isCFSBModalOpened = useCountdownFSBonusStore((s) => s.isModalOpened)
    const isMenuOpen = useNavigationStore((s) => s.isMenuOpen)
    const isMultiChoiceToggled = useBuyBonusStore((s) => s.isMultiChoiceToggled)
    const selectedFeature = useBuyBonusStore((s) => s.selectedFeature)
    const selectedFeatureIndex = useBuyBonusStore((s) => s.selectedFeatureIndex)
    const findElementByImageName = useBuyBonusStore((s) => s.findElementByImageName)
    const showBuyBonusModal = useBuyBonusStore((s) => s.showModal)
    const cachedImages = useBuyBonusStore((s) => s.cachedImages)

    const bbButtonStyles = useConfigurationStore((s) => s.getComponentConfig('BuyBonusButton').styles)
    const bbButtonDimentions = useConfigurationStore((s) => s.getComponentConfig('BuyBonusButton').dimensions)
    const { secondaryButtonPosition: bbButtonPosition } = useAmuseBoostPromoWidgetPositions()

    const bbCompactButtonStyles = useConfigurationStore((s) => s.getComponentConfig('BuyBonusButtonCompact').styles)
    const bbCompactButtonDimentions = useConfigurationStore(
        (s) => s.getComponentConfig('BuyBonusButtonCompact').dimensions
    )
    const bbCompactButtonPosition = useConfigurationStore((s) => s.getComponentConfig('BuyBonusButtonCompact').position)

    const buyBonusComponent = useRef<HTMLButtonElement | null>(null)
    const buyBonusLabelRef = useRef<HTMLDivElement | null>(null)
    const activeLabelRef = useRef<HTMLDivElement | null>(null)
    const buyBonusText = 'BUY BONUS'

    const buyBonusButtonDimensions = isCompact ? { ...bbCompactButtonDimentions } : { ...bbButtonDimentions }
    const buyBonusButtonPosition = isCompact ? { ...bbCompactButtonPosition } : { ...bbButtonPosition }
    const { isComponentInteractive, opacity } = useOpacityToggle(
        ChangeOpacityComponents.BUY_BONUS_BUTTON,
        buyBonusComponent,
        0,
        false
    )
    const isBonusModalOpenedRef =
        showBuyBonusModal || isFBBModalOpened || isFSBModalOpened || isLSBModalOpened || isCFSBModalOpened
    const activeConditions = isComponentInteractive && !isBonusModalOpenedRef && (isLongPress || isSpaceKeyPressed)
    const buyBonusTextStyle = isCompact ? 'mt-[20px]' : shouldTheFeatureStayActive ? 'mt-[10px]' : '' // TODO: this will be removed when the design is finalized
    const buyBonusTextSizeTop = isCompact ? '1.1em' : '1.4em'
    const buyBonusTextSizeBottom = isCompact ? '0.8em' : '1.1em'

    const buyBonusLabelBounds = isCompact ? { width: 100, height: 125 } : { width: 116, height: 130 }

    useShrinkTextToFit({
        refresh: [isMultiChoiceToggled, selectedFeature, isCompact],
        designFontSize: 30,
        bounds: buyBonusLabelBounds, // TODO: use the actual dimensions from the config
        textRef: buyBonusLabelRef,
    })

    useShrinkTextToFit({
        refresh: [isMultiChoiceToggled, selectedFeature],
        designFontSize: 26,
        bounds: { width: 108, height: 130 }, // TODO: use the actual dimensions from the config
        textRef: activeLabelRef,
    })

    const renderBackgroundImage = () => {
        if (shouldTheFeatureStayActive) {
            return isMultiChoiceToggled
                ? buyBonusActiveImage
                : isCompact
                  ? buyBonusDefaultCompactImage
                  : buyBonusDefaultImage
        }

        return isCompact ? buyBonusDefaultCompactImage : buyBonusImage
    }

    const renderMainText = () => {
        return (
            <div ref={buyBonusLabelRef} className={buyBonusTextStyle}>
                {renderMultilineText(
                    buyBonusText,
                    { fontSize: buyBonusTextSizeTop },
                    { fontSize: buyBonusTextSizeBottom }
                )}
            </div>
        )
    }

    const renderToggledFeature = () => {
        const imageData =
            findElementByImageName(`buy-bonus-button-image-card-0${selectedFeatureIndex + 1}.png`) ||
            findElementByImageName(SELECTED_FEATURE_IMAGE_NAME)

        const backgroundStyles =
            buyBonusConfig.hasGuaranteedWin && imageData
                ? buildAtlasImageStyles(imageData.atlasName, imageData.value, cachedImages[imageData.atlasName])
                : {
                      backgroundImage: `url(${bonusFeatureIcon})`,
                      width: `110px`,
                      height: `102px`,
                      backgroundRepeat: 'no-repeat',
                  }

        return (
            <div className="relative flex flex-col items-center w-full h-full mt-[-10px]">
                <div style={{ ...backgroundStyles }} className="w-full h-full object-cover rounded-t-[8px]" />
            </div>
        )
    }

    return (
        <button
            id="buy-bonus-button"
            ref={buyBonusComponent}
            className={cn(
                'absolute focus:outline-none',
                isCompact ? (bbCompactButtonStyles ?? '') : (bbButtonStyles ?? '')
            )}
            onClick={withAutoBlur(onClick)}
            style={{
                ...buyBonusButtonDimensions,
                ...buyBonusButtonPosition,
                zIndex: isAutoplaySectionOpened || isBetSectionOpened || isMenuOpen ? 10 : 11,
                // Quoted on purpose: Vite's `?inline` emits SVGs as `data:image/svg+xml,...` with
                // attribute quotes rewritten to `'`, and an unquoted CSS url() may not contain them.
                backgroundImage: `url("${renderBackgroundImage()}")`,
                display: 'flex',
                justifyContent: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                padding: isCompact ? '' : '10px 10px',
                opacity: activeConditions ? 0.5 : opacity,
                pointerEvents: activeConditions ? 'none' : 'auto',
            }}
        >
            <div className="text-white font-bold leading-none flex flex-col items-center justify-between w-full h-full [text-shadow:0px_2px_0px_black]">
                {isMultiChoiceToggled ? renderToggledFeature() : renderMainText()}
            </div>
        </button>
    )
}

export default BuyBonusButton
